import { useEffect } from 'react';

/**
 * Wires up the document-shell styling of the public profile — the parts React
 * doesn't render, so they can't be reached with component CSS:
 *
 *  - `chrome`: the browser UI colour (`theme-color` meta) and the html/body
 *    overscroll backdrop. A single solid colour (theme-color can't be a gradient).
 *  - `pageBackground`: the user's themed background (gradient or solid), exposed
 *    as the `--lk-page-bg` custom property. CSS owns *where* it applies
 *    (`.user-links-layout`); JS only injects the value.
 *
 * All values are restored on unmount, so the effect is scoped to the page.
 * Pass falsy values to no-op.
 */
export function useThemeColor(chrome?: string | null, pageBackground?: string | null, loading?: boolean) {
  useEffect(() => {
    const root = document.documentElement;

    const meta =
      document.querySelector<HTMLMetaElement>('meta[name="theme-color"]') ??
      document.head.appendChild(
        Object.assign(document.createElement('meta'), { name: 'theme-color' }),
      );

    const prev = {
      content: meta.getAttribute('content'),
      body: document.body.style.background,
      html: root.style.backgroundColor,
      pageBg: root.style.getPropertyValue('--lk-page-bg'),
      bodyHeight: document.body.style.height,
    };

    if (chrome && !loading) {
      meta.setAttribute('content', chrome);
      root.style.backgroundColor = chrome;
    }
    if (pageBackground && !loading) {
      root.style.setProperty('--lk-page-bg', pageBackground);
      // Body carries the user's own themed background (gradient/solid), not the
      // dark chrome — so the page/overscroll reads as their colour.
      document.body.style.background = pageBackground;
    }
    // index.css locks `body { height: 100% }`, which clips the themed background
    // to the viewport. On this route let the body grow so the background covers
    // the full scrollable page (restored on leave).
    document.body.style.height = 'auto';

    return () => {
      if (prev.content === null) meta.remove();
      else meta.setAttribute('content', prev.content);
      document.body.style.background = prev.body;
      document.body.style.height = prev.bodyHeight;
      root.style.backgroundColor = prev.html;
      if (prev.pageBg) root.style.setProperty('--lk-page-bg', prev.pageBg);
      else root.style.removeProperty('--lk-page-bg');
    };
  }, [chrome, pageBackground, loading]);
}
